import { useEffect, useState } from 'react';
import { mensajeDe, normalizarTelefonoCO, tiposDocumentoDe, validarDocumento } from '@tanteo/compartido';
import { actualizarNegocioRpc } from '../lib/api-negocio';
import {
  actualizarMisDatos,
  actualizarMiPerfilPanel,
  confirmarCambioDeCorreo,
  pedirCambioDeCorreo,
} from '../lib/api-perfil';
import { miPerfilPlataforma, type PerfilPlataforma } from '../lib/api-panel';
import { LARGO_CODIGO } from '../lib/codigo';
import { useApp, type Membresia } from '../lib/contexto';
import { Boton, BotonEnlace, Campo, CampoCelular, CampoCodigo, CampoLista, FilaCampos, MensajeError, Tarjeta, TextoAyuda } from '../ui/base';

/** Los documentos con que se identifica una persona natural; el NIT identifica a una empresa. */
const TIPOS_PERSONA = tiposDocumentoDe('representante');


/**
 * `conIdentidadDePlataforma` la pasa solo la ruta que se dibuja sin negocio activo. Dentro de un
 * negocio esta pantalla es la de ese negocio: los datos del panel no se ven ni se editan aquí.
 */
export function Perfil({ conIdentidadDePlataforma = false }: { conIdentidadDePlataforma?: boolean }) {
  const { sesion, negocio, recargarMembresias } = useApp();

  return (
    <div className="space-y-4">
      <h1 className="text-titulo font-extrabold">Perfil</h1>

      {/* Una sección por contexto donde la persona tiene identidad: su negocio, el panel si opera la
          plataforma, y el correo de la cuenta al final. En persona natural el negocio y su titular son
          la misma identidad, así que el propietario tiene una sola tarjeta; quien no es dueño edita
          solo sus datos. */}
      {negocio?.rol === 'propietario' ? (
        <DatosDelNegocio negocio={negocio} onGuardado={recargarMembresias} />
      ) : (
        negocio && <MisDatosDelNegocio negocio={negocio} onGuardado={recargarMembresias} />
      )}

      {conIdentidadDePlataforma && <MisDatosDelPanel />}

      <CorreoDeLaCuenta correoActual={sesion?.user.email ?? ''} />
    </div>
  );
}

/**
 * Los datos de la persona en ESE negocio: el nombre que sale en los comprobantes que registre
 * desde ahora, y su identidad. Los comprobantes ya emitidos conservan el nombre congelado.
 */
function MisDatosDelNegocio({ negocio, onGuardado }: { negocio: Membresia; onGuardado: () => Promise<void> }) {
  const partes = negocio.nombreVisible.trim().split(/\s+/).filter(Boolean);
  const [nombres, setNombres] = useState(negocio.nombres ?? partes[0] ?? '');
  const [apellidos, setApellidos] = useState(negocio.apellidos ?? partes.slice(1).join(' '));
  // El campo muestra el +57 aparte, así que el valor son los diez dígitos nacionales.
  const [telefono, setTelefono] = useState((negocio.telefono ?? '').replace(/^\+57/, ''));
  const [docTipo, setDocTipo] = useState(negocio.documentoTipo ?? '13');
  const [docNumero, setDocNumero] = useState(negocio.documentoNumero ?? '');
  const [error, setError] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setGuardado(false);
    if (nombres.trim().length < 2 || apellidos.trim().length < 2) {
      setError('Escribe tus nombres y apellidos (mínimo dos letras cada uno).');
      return;
    }
    // El documento se valida solo si se escribió; el celular es obligatorio, porque es el canal para
    // avisarle a alguien de algo de su negocio. Las mismas reglas que aplica el servidor.
    if (docNumero.trim() && !validarDocumento(docTipo, docNumero.trim())) {
      setError(mensajeDe('DOCUMENTO_INVALIDO'));
      return;
    }
    if (!telefono.trim()) {
      setError(mensajeDe('CELULAR_REQUERIDO'));
      return;
    }
    if (normalizarTelefonoCO(telefono) === null) {
      setError(mensajeDe('TELEFONO_INVALIDO'));
      return;
    }
    setOcupado(true);
    try {
      await actualizarMisDatos(negocio.negocioId, {
        nombres,
        apellidos,
        telefono,
        documentoTipo: docTipo,
        documentoNumero: docNumero,
      });
      await onGuardado();
      setGuardado(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar tus datos.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <h2 className="mb-1 font-extrabold">Tus datos en {negocio.negocioNombre}</h2>
      <TextoAyuda>
        Tu rol aquí es <strong>{negocio.rol}</strong>. Tu nombre sale como quien atendió en los comprobantes
        que registres; cambiarlo no toca los que ya emitiste.
      </TextoAyuda>
      {/* Mismo orden y mismas etiquetas que el formulario de persona del mostrador y del CRM
          (`ui/FormularioTercero.tsx`): documento, nombre, contacto. */}
      <form onSubmit={(e) => void guardar(e)} noValidate className="space-y-3">
        <FilaCampos>
          <CampoLista etiqueta="Tipo de documento" value={docTipo} onChange={(e) => setDocTipo(e.target.value)}>
            {TIPOS_PERSONA.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.nombre}
              </option>
            ))}
          </CampoLista>
          <Campo
            etiqueta="Número"
            value={docNumero}
            onChange={(e) => setDocNumero(e.target.value)}
            inputMode="numeric"
            className="num"
          />
        </FilaCampos>

        <FilaCampos>
          <Campo
            etiqueta="Nombres"
            value={nombres}
            onChange={(e) => setNombres(e.target.value)}
            autoComplete="given-name"
          />
          <Campo
            etiqueta="Apellidos"
            value={apellidos}
            onChange={(e) => setApellidos(e.target.value)}
            autoComplete="family-name"
          />
        </FilaCampos>

        <FilaCampos>
          <CampoCelular
            etiqueta="Celular"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            className="num"
          />
        </FilaCampos>

        <MensajeError>{error}</MensajeError>
        {guardado && <p className="text-success mt-3 text-contenido font-semibold">Datos actualizados.</p>}
        <div className="mt-4">
          <Boton type="submit" disabled={ocupado} data-e2e="perfil-guardar">
            {ocupado ? 'Guardando…' : 'Guardar cambios'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  );
}

/**
 * El negocio y su titular en una tarjeta: en persona natural son la misma identidad, y
 * `actualizar_negocio` escribe negocio y membresía en una transacción. El celular es obligatorio.
 */
function DatosDelNegocio({ negocio, onGuardado }: { negocio: Membresia; onGuardado: () => Promise<void> }) {
  const [nombre, setNombre] = useState(negocio.negocioNombre);
  const [docTipo, setDocTipo] = useState(negocio.documentoTipo ?? negocio.negocioDocTipo ?? '13');
  const [docNumero, setDocNumero] = useState(negocio.documentoNumero ?? negocio.negocioDocNumero ?? '');
  const [nombres, setNombres] = useState(negocio.nombres ?? '');
  const [apellidos, setApellidos] = useState(negocio.apellidos ?? '');
  // El campo muestra el +57 aparte, así que el valor son los diez dígitos nacionales.
  const [telefono, setTelefono] = useState((negocio.telefono ?? negocio.negocioTelefono ?? '').replace(/^\+57/, ''));
  const [error, setError] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setGuardado(false);
    if (nombre.trim().length < 3) {
      setError('El nombre del negocio necesita al menos 3 letras.');
      return;
    }
    if (!validarDocumento(docTipo, docNumero.trim())) {
      setError(mensajeDe('DOCUMENTO_INVALIDO'));
      return;
    }
    if (nombres.trim().length < 2 || apellidos.trim().length < 2) {
      setError('Escribe tus nombres y apellidos (mínimo dos letras cada uno).');
      return;
    }
    if (normalizarTelefonoCO(telefono) === null) {
      setError(mensajeDe('TELEFONO_INVALIDO'));
      return;
    }
    setOcupado(true);
    try {
      await actualizarNegocioRpc(negocio.negocioId, {
        nombre,
        nombres,
        apellidos,
        docTipo,
        docNumero,
        telefono,
      });
      await onGuardado();
      setGuardado(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos del negocio.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <h2 className="mb-3 font-extrabold">Datos de tu negocio</h2>
      <form onSubmit={(e) => void guardar(e)} noValidate className="space-y-3">
        <Campo
          etiqueta="Nombre del negocio"
          ayuda="Así te ven tus clientes y te identificas en los comprobantes."
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />

        <FilaCampos>
          <CampoLista etiqueta="Tipo de documento" value={docTipo} onChange={(e) => setDocTipo(e.target.value)}>
            {TIPOS_PERSONA.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.nombre}
              </option>
            ))}
          </CampoLista>
          <Campo
            etiqueta="Número"
            value={docNumero}
            onChange={(e) => setDocNumero(e.target.value)}
            inputMode="numeric"
            className="num"
          />
        </FilaCampos>

        <FilaCampos>
          <Campo etiqueta="Nombres" value={nombres} onChange={(e) => setNombres(e.target.value)} autoComplete="given-name" />
          <Campo
            etiqueta="Apellidos"
            value={apellidos}
            onChange={(e) => setApellidos(e.target.value)}
            autoComplete="family-name"
          />
        </FilaCampos>

        {/* En la rejilla de dos columnas, para que mida lo mismo que el tipo de documento. */}
        <FilaCampos>
          <CampoCelular value={telefono} onChange={(e) => setTelefono(e.target.value)} className="num" />
        </FilaCampos>

        <MensajeError>{error}</MensajeError>
        {guardado && <p className="text-success mt-3 text-contenido font-semibold">Datos actualizados.</p>}
        <div className="mt-4">
          <Boton type="submit" disabled={ocupado} data-e2e="negocio-guardar">
            {ocupado ? 'Guardando…' : 'Guardar cambios'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  );
}

/**
 * Los datos de quien opera el panel. Cuatro campos y el rol en lectura, como la sección de
 * plataforma del perfil de un precedente del equipo.
 */
function MisDatosDelPanel() {
  const [perfil, setPerfil] = useState<PerfilPlataforma | null>(null);
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [telefono, setTelefono] = useState('');
  const [cargo, setCargo] = useState('');
  const [error, setError] = useState('');
  const [guardado, setGuardado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    miPerfilPlataforma()
      .then((p) => {
        setPerfil(p);
        setNombre(p.nombre ?? '');
        setApellido(p.apellido ?? '');
        setTelefono((p.telefono ?? '').replace(/^\+57/, ''));
        setCargo(p.cargo ?? '');
      })
      .catch(() => setPerfil({ rol: null, nombre: null, apellido: null, telefono: null, cargo: null, permisos: [] }));
  }, []);

  if (!perfil?.rol) return null;

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setGuardado(false);
    if (nombre.trim().length < 3) {
      setError('Escribe tu nombre (mínimo tres letras).');
      return;
    }
    if (telefono.trim() && normalizarTelefonoCO(telefono) === null) {
      setError(mensajeDe('TELEFONO_INVALIDO'));
      return;
    }
    setOcupado(true);
    try {
      const guardadoEn = await actualizarMiPerfilPanel({ nombre, apellido, telefono, cargo });
      setNombre(guardadoEn.nombre);
      setGuardado(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar tus datos.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <h2 className="mb-1 font-extrabold">Tus datos en el panel</h2>
      <TextoAyuda>
        Con esto te ve el resto del equipo que opera el panel. Tu rol es{' '}
        <strong>{perfil.rol === 'admin' ? 'administrador' : 'analista'}</strong>, y lo asigna otro administrador.
      </TextoAyuda>
      <form onSubmit={(e) => void guardar(e)} noValidate className="space-y-3">
        <FilaCampos>
          <Campo
            etiqueta="Nombres"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            autoComplete="given-name"
          />
          <Campo
            etiqueta="Apellidos"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
            autoComplete="family-name"
          />
        </FilaCampos>
        <FilaCampos>
          <CampoCelular
            etiqueta="Celular (opcional)"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            className="num"
          />
          <Campo
            etiqueta="Cargo (opcional)"
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
            placeholder="Soporte, operaciones…"
          />
        </FilaCampos>
        <MensajeError>{error}</MensajeError>
        {guardado && <p className="text-success mt-3 text-contenido font-semibold">Datos actualizados.</p>}
        <div className="mt-4">
          <Boton type="submit" disabled={ocupado} data-e2e="perfil-panel-guardar">
            {ocupado ? 'Guardando…' : 'Guardar cambios'}
          </Boton>
        </div>
      </form>
    </Tarjeta>
  );
}

type PasoCorreo = 'inicio' | 'nuevo' | 'codigo_actual' | 'codigo_nuevo' | 'listo';

/**
 * Cambio del correo de la cuenta por pasos, un código a la vez: primero la dirección actual y
 * después la nueva. Pedir los dos códigos juntos obliga a manejar dos buzones al tiempo.
 */
function CorreoDeLaCuenta({ correoActual }: { correoActual: string }) {
  const [paso, setPaso] = useState<PasoCorreo>('inicio');
  const [correoNuevo, setCorreoNuevo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  function volverAlInicio() {
    setPaso('inicio');
    setCorreoNuevo('');
    setCodigo('');
    setError('');
  }

  async function pedir() {
    setError('');
    const limpio = correoNuevo.trim().toLowerCase();
    if (limpio === correoActual.trim().toLowerCase()) {
      setError('Ese ya es tu correo.');
      return;
    }
    setOcupado(true);
    try {
      await pedirCambioDeCorreo(limpio);
      setCodigo('');
      setPaso('codigo_actual');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos iniciar el cambio de correo.');
    } finally {
      setOcupado(false);
    }
  }

  /** Verifica el código de la dirección que lo recibió, y avanza al siguiente paso. */
  async function verificar() {
    setError('');
    setOcupado(true);
    const destinatario = paso === 'codigo_actual' ? correoActual : correoNuevo;
    try {
      await confirmarCambioDeCorreo(destinatario, codigo);
      setCodigo('');
      setPaso(paso === 'codigo_actual' ? 'codigo_nuevo' : 'listo');
    } catch (e) {
      setCodigo('');
      setError(e instanceof Error ? e.message : 'El código no es válido o ya venció.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <h2 className="mb-1 font-extrabold">Correo electrónico</h2>
      <p className="mb-3 text-contenido">
        Correo actual: <strong>{correoActual || '—'}</strong>
      </p>

      {paso === 'inicio' && (
        <Boton variante="contorno" onClick={() => setPaso('nuevo')} data-e2e="cambiar-correo">
          Cambiar correo electrónico
        </Boton>
      )}

      {paso === 'nuevo' && (
        <div className="space-y-3">
          <Campo
            etiqueta="Correo nuevo"
            type="email"
            value={correoNuevo}
            onChange={(e) => setCorreoNuevo(e.target.value)}
            autoComplete="email"
            className="max-w-md"
          />
          <p className="text-muted text-contenido">
            Por seguridad enviaremos <strong>dos códigos</strong>: uno a tu correo actual y otro al nuevo. Necesitas
            entrar a los dos buzones.
          </p>
          <div className="flex gap-2">
            <Boton onClick={() => void pedir()} disabled={ocupado || !correoNuevo.trim()} data-e2e="pedir-codigos">
              {ocupado ? 'Enviando…' : 'Enviar códigos'}
            </Boton>
            <Boton variante="contorno" onClick={volverAlInicio} disabled={ocupado}>
              Cancelar
            </Boton>
          </div>
        </div>
      )}

      {(paso === 'codigo_actual' || paso === 'codigo_nuevo') && (
        <div className="space-y-3">
          <p className="text-contenido">
            Paso {paso === 'codigo_actual' ? '1' : '2'} de 2 — escribe el código que llegó a{' '}
            <strong>{paso === 'codigo_actual' ? correoActual : correoNuevo}</strong>.
          </p>
          <CampoCodigo
            largo={LARGO_CODIGO}
            valor={codigo}
            onCambio={setCodigo}
            etiqueta="Código de verificación"
            e2e="codigo-correo"
          />
          <div className="flex gap-2">
            <Boton
              onClick={() => void verificar()}
              disabled={ocupado || codigo.length !== LARGO_CODIGO}
              data-e2e="verificar-codigo"
            >
              {ocupado ? 'Verificando…' : 'Verificar'}
            </Boton>
            <BotonEnlace onClick={volverAlInicio} disabled={ocupado}>
              Cancelar el cambio
            </BotonEnlace>
          </div>
          <p className="text-muted text-contenido">
            Si cierras esta página a medias, tu cuenta no queda bloqueada: el cambio solo se aplica cuando los dos
            códigos quedan verificados.
          </p>
        </div>
      )}

      {paso === 'listo' && (
        <div className="space-y-2">
          <p className="text-success font-bold">✓ Correo actualizado</p>
          <p className="text-muted text-contenido">Usa el correo nuevo la próxima vez que entres.</p>
          <BotonEnlace onClick={volverAlInicio}>Volver</BotonEnlace>
        </div>
      )}

      <MensajeError>{error}</MensajeError>
    </Tarjeta>
  );
}
